import jwt from "jsonwebtoken";
import { isTokenExpired, secondsUntilExpiry } from "@/utils/tokenUtils";
import type { JwtPayload, JwtValidationResult } from "./types";

/**
 * Verifies a JWT and returns its payload, or null if invalid.
 * @param token The JWT token.
 * @param secret The secret to verify the token with.
 * @returns The payload of the token, or null if verification fails.
 */
export const verifyJwt = (token: string, secret: string): JwtPayload | null => {
  try {
    if (!token || !secret) {
      throw new Error("JWT token and secret are required to verify a token");
    }

    return jwt.verify(token, secret) as JwtPayload;
  } catch (error) {
    console.error("[JwtService] JWT verification failed:", error);
    return null;
  }
};

/**
 * Decodes a JWT without verifying signature.
 * @param token The JWT token.
 * @returns The decoded JWT payload, or null if decoding fails.
 */
export const decodeJwt = (token: string): JwtPayload | null => {
  try {
    if (!token) {
      throw new Error("[JwtService] Error in decodeJwt: JWT token is required to decode a token");
    }

    return jwt.decode(token) as JwtPayload;
  } catch (error) {
    console.error("[JwtService] JWT decoding failed:", error);
    return null;
  }
};

/**
 * Validates a token for format (decoding and verifying the signature) and expiration.
 * @param token The JWT token.
 * @returns The validation result.
 */
export const validateJwt = (token: string): JwtValidationResult => {
  if (!token) {
    return { isValid: false, error: "Token is required and must be a string" };
  }

  const payload = decodeJwt(token);
  if (!payload) {
    return { isValid: false, error: "Invalid token format" };
  }

  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  if (payload.exp && payload.exp < currentTimeInSeconds) {
    return { isValid: false, error: "Token has expired", payload };
  }

  return { isValid: true, payload };
};

/**
 * Signs a JWT with the given data and secret.
 * @param payload The payload to sign.
 * @param secret The secret to sign the payload with.
 * @param expiresIn The expiration time.
 * @returns The signed JWT.
 */
export const signJwt = (
  payload: object,
  secret: string,
  expiresIn: string | number = "15m"
): string => {
  try {
    if (!secret) {
      throw new Error("[JwtService] Error in signJwt: JWT secret is required to sign a token");
    }

    return jwt.sign(payload, secret, { expiresIn } as jwt.SignOptions);
  } catch (error) {
    throw new Error(`[JwtService] Error in signJwt: Failed to sign JWT: ${error}`);
  }
};

/**
 * Checks if a token is expired.
 * @param token The JWT token.
 * @returns True if the token is expired, false otherwise.
 */
export const isJwtExpired = (token: string): boolean => {
  try {
    if (!token) {
      throw new Error(
        "[JwtService] Error in isJwtExpired: JWT token is required to check if it is expired"
      );
    }

    const payload = decodeJwt(token);

    if (!payload?.exp) {
      return true;
    }

    return isTokenExpired(payload.exp);
  } catch (error) {
    console.error("[JwtService] Error decoding JWT in isJwtExpired:", error);
    return true;
  }
};

/**
 * Returns seconds until token expiry.
 * @param token The JWT token.
 * @returns The seconds until token expiry.
 */
export const getJwtTimeToExpiry = (token: string): number => {
  try {
    if (!token) {
      throw new Error(
        "[JwtService] Error in getJwtTimeToExpiry: JWT token is required to get the time to expiry"
      );
    }

    const payload = decodeJwt(token);
    if (!payload?.exp) {
      return 0;
    }

    return secondsUntilExpiry(payload.exp);
  } catch (error) {
    console.error("[JwtService] Error in getJwtTimeToExpiry:", error);
    return 0;
  }
};
